import {
  breakpointLg,
  breakpointMd,
  breakpointSm,
  breakpointXl,
  breakpointXs
} from '@/styles/variables.module.scss';

/**
 * Extracts the number of pixels from an SCSS dimension with `px` unit.
 *
 * @param value - An SCSS dimension with `px` unit.
 * @returns The number of pixels.
 * @throws When the breakpoint cannot be parsed.
 * @example
 * parseSCSSPixels('1024px') // 1024
 */
export function parseSCSSPixels(value: string): number {
  // '0' is a special case, as it is commonly written without the unit ('0' instead of '0px')
  if (value === '0') return 0;

  if (!value.endsWith('px')) {
    throw new Error(
      `Cannot parse breakpoint "${value}", since it doesn't end with "px"`
    );
  }

  const px = parseInt(value.slice(0, -2), 10);
  if (!Number.isFinite(px) || Number.isNaN(px)) {
    throw new Error(
      `Cannot parse breakpoint "${value}", since it's not a valid number. Tried "${px}"`
    );
  }

  return px;
}

export const breakpointSizes = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'] as const;

export const breakpointSizeToIndexMap = new Map(
  breakpointSizes.map((size, index) => [size, index])
);

/**
 * Represents a small range of screen widths.
 * The value typically refers to some configured value, up to the next breakpoint.
 * @example
 * 'md' // represents screens from 768px to 1023px wide.
 */
export type BreakpointSize = (typeof breakpointSizes)[number];

export const breakpointSizeRanges = new Set([
  /* eslint-disable prettier/prettier -- This is more readable. */
  "xxs:xs", "xxs:sm", "xxs:md", "xxs:lg", // "xxs:xl" is excluded because it spans the entire range
  "xs:sm" , "xs:md" , "xs:lg" , "xs:xl",
  "sm:md" , "sm:lg" , "sm:xl",
  "md:lg" , "md:xl",
  "lg:xl"
  /* eslint-enable prettier/prettier */
] as const satisfies Array<`${BreakpointSize}:${BreakpointSize}`>);

/**
 * Represents a range of breakpoint sizes, inclusive.
 * @example
 * 'xxs:sm' // represents breakpoint sizes "xxs", "xs", & "sm" (from 0px to 767px wide).
 */
export type BreakpointSizeRange =
  typeof breakpointSizeRanges extends Set<infer T> ? T : never;

export const breakpointDeviceToSize = {
  phone: ['xxs', 'xs'],
  tablet: ['sm', 'md'],
  desktop: ['lg', 'xl']
} as const satisfies Record<string, Array<BreakpointSize>>;

/**
 * Represents a range of breakpoint sizes based on device type.
 * @example
 * 'tablet' // represents breakpoint sizes "sm" & "md" (640px to 1023px)
 * */
export type BreakpointDevice = keyof typeof breakpointDeviceToSize;

/** Represents a key that can be used to configure a breakpoint to match. */
export type BreakpointMediaKey =
  | BreakpointSize
  | BreakpointSizeRange
  | BreakpointDevice;

/**
 * Creates a {@link MediaQueryList} object which matches the given minimum width.
 * @param w - The window object.
 * @param width - The minimum width to match in pixels as CSS value. Ex: '1024px'.
 * @returns A {@link MediaQueryList} object.
 */
function createMinWidthMediaQuery(w: Window, width: string): MediaQueryList {
  return w.matchMedia(`(min-width: ${parseSCSSPixels(width)}px)`);
}

let breakpointMediaQueries: {
  [key in Exclude<BreakpointSize, 'xxs'>]: MediaQueryList;
};

/**
 * Gets the matching breakpoint size for a given width.
 * @param w - The window object. Signals that this
 * function should only be called in a browser environment.
 * @returns The matching breakpoint size.
 */
export function getBreakpointSize(w: Window): BreakpointSize {
  // Initializes the breakpointMediaQueries object only once.
  breakpointMediaQueries ??= {
    // MediaQueryLists are "live" objects. Their `matches` property
    // will update automatically when the viewport size changes.
    xs: createMinWidthMediaQuery(w, breakpointXs),
    sm: createMinWidthMediaQuery(w, breakpointSm),
    md: createMinWidthMediaQuery(w, breakpointMd),
    lg: createMinWidthMediaQuery(w, breakpointLg),
    xl: createMinWidthMediaQuery(w, breakpointXl)
  };

  if (breakpointMediaQueries.xl.matches) return 'xl';
  if (breakpointMediaQueries.lg.matches) return 'lg';
  if (breakpointMediaQueries.md.matches) return 'md';
  if (breakpointMediaQueries.sm.matches) return 'sm';
  if (breakpointMediaQueries.xs.matches) return 'xs';
  return 'xxs';
}
